// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { HiddenProps } from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import { DivWrapper } from "@stanion/XDL/constants";
import Menu from "@stanion/XDL/Icons/Menu";
import { color, layoutContainer } from "@stanion/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    setInitialValues,
    setNavDrawerIsOpen,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden?: HiddenProps;
    // XNG-Changes
    actionsContainer?: InjectableCss;
    searchWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    mobile?: {
        container?: InjectableCss;
        logoMenu?: InjectableCss;
        searchMenuWrapper?: InjectableCss;
        searchWrapper?: InjectableCss;
        actionsWrapper?: InjectableCss;
        menuTriggerButton?: ButtonPresentationProps;
    };
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            padding-top: 18px;
            padding-bottom: 18px;
            ${layoutContainer}
        `,
    },
    actionsContainer: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-grow: 1;
            flex-wrap: wrap;
            justify-content: space-between;
        `,
    },
    logoWrapper: {
        css: css`
            margin-right: 58px;
            img {
                height: 70px;
                width: 175px;
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            flex-basis: 100%;
            margin-bottom: 15px;
            & > ${DivWrapper} {
                display: flex;
                justify-content: space-between;
                align-items: center;
                & > div:not(:last-child) {
                    display: flex;
                    margin-right: 20px;
                }
            }
        `,
    },
    searchWrapper: {
        css: css`
            flex-grow: 1;
            max-width: 492px;
            margin-right: 16px;
        `,
    },
    actionsWrapper: {
        css: css`
            & > ${DivWrapper} {
                display: flex;
                & > div:not(:last-child) {
                    margin-right: 20px;
                }
            }
        `,
    },
    mobile: {
        container: {
            css: css`
                width: 100%;
                display: flex;
                justify-content: space-between;
                align-items: center;
            `,
        },
        logoMenu: {
            css: css`
                display: flex;
                align-items: center;
                > div:first-child {
                    width: auto;
                }
            `,
        },
        searchWrapper: {
            css: css`
                display: flex;
                flex-basis: 100%;
                justify-content: center;
                background: ${color("primary")};
                margin-top: 40px;
                > div {
                    max-width: 420px;
                }
            `,
        },
        actionsWrapper: {
            css: css`
                & > ${DivWrapper} {
                    display: flex;
                    & > div:not(:last-child) {
                        margin-right: 0;
                    }
                }
            `,
        },
    },
};

const LogoLinksSearch: React.FC<Props> = ({ id, setNavDrawerIsOpen, isTabletScreen, setInitialValues }) => {
    const openDrawer = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    return (
        <StyledWrapper {...logoLinksSearchStyles.container}>
            {isTabletScreen ? (
                <StyledWrapper {...logoLinksSearchStyles.mobile?.container}>
                    <StyledWrapper {...logoLinksSearchStyles.mobile?.logoMenu}>
                        <Button
                            onClick={openDrawer}
                            {...logoLinksSearchStyles.mobile?.menuTriggerButton}
                            data-test-selector="expandMobileMenu"
                        >
                            <ButtonIcon src={Menu} />
                            <VisuallyHidden>{translate("menu")}</VisuallyHidden>
                        </Button>
                        <Zone contentId={id} zoneName="Logo" />
                    </StyledWrapper>
                    <StyledWrapper {...logoLinksSearchStyles.mobile?.actionsWrapper}>
                        <Zone contentId={id} zoneName="Actions" />
                    </StyledWrapper>
                    <StyledWrapper {...logoLinksSearchStyles.mobile?.searchWrapper}>
                        <Zone contentId={id} zoneName="Search" />
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <>
                    <StyledWrapper {...logoLinksSearchStyles.logoWrapper}>
                        <Zone contentId={id} zoneName="Logo" />
                    </StyledWrapper>
                    <StyledWrapper {...logoLinksSearchStyles.actionsContainer}>
                        <StyledWrapper {...logoLinksSearchStyles.searchMenuWrapper}>
                            <Zone contentId={id} zoneName="LinkList" />
                        </StyledWrapper>
                        <StyledWrapper {...logoLinksSearchStyles.searchWrapper}>
                            <Zone contentId={id} zoneName="Search" />
                        </StyledWrapper>
                        <StyledWrapper {...logoLinksSearchStyles.actionsWrapper}>
                            <Zone contentId={id} zoneName="Actions" />
                        </StyledWrapper>
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
